import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Calendar, Clock } from "lucide-react";
import { useLanguageNavigation } from "@/components/LanguageRouter";
import { blogPosts, getLocalizedField } from "@/data/blogPosts";
import { BlogListJsonLd } from "@/components/seo/JsonLd";

// Listing-specific images (from original shelpuk.com/blog)
import vendorlandListing from "@/assets/blog/listing/vendorland-listing.webp";
import tgiLlama33Listing from "@/assets/blog/listing/tgi-llama33-listing.webp";
import buyVsBuildListing from "@/assets/blog/listing/buy-vs-build-listing.webp";
import llama32Listing from "@/assets/blog/listing/llama-3-2-listing.png";
import exploreAiListing from "@/assets/blog/listing/explore-ai-listing.webp";
import swiftListing from "@/assets/blog/listing/swift-listing.webp";
import unslothListing from "@/assets/blog/listing/unsloth-listing.webp";
import loraListing from "@/assets/blog/listing/lora-listing.webp";
import gemmaListing from "@/assets/blog/listing/gemma-listing.webp";
import multilingualLlmsListing from "@/assets/blog/listing/multilingual-llms-listing.webp";
import llama2MultilingualListing from "@/assets/blog/listing/llama2-multilingual-listing.webp";
import clutchListing from "@/assets/blog/listing/clutch-listing.webp";
import tangoListing from "@/assets/blog/listing/tango-listing.webp";
import competentMgmtListing from "@/assets/blog/listing/competent-mgmt-listing.webp";
import chatgptListing from "@/assets/blog/listing/chatgpt-listing.webp";
import networkEffectListing from "@/assets/blog/listing/network-effect-listing.webp";
import aiStrategiesListing from "@/assets/blog/listing/ai-strategies-listing.webp";
import aiDesignsListing from "@/assets/blog/listing/ai-designs-listing.webp";
import aiBusinessListing from "@/assets/blog/listing/ai-business-listing.webp";

// Fallback to in-post hero images for posts without separate listing images
import aiDevCulture from "@/assets/blog/ai-development-culture.webp";
import aiVirtuousCycle from "@/assets/blog/ai-virtuous-cycle.webp";
import blueRiverTech from "@/assets/blog/blue-river-technology.webp";

import claudeCodeListing from "@/assets/blog/listing/claude-code-quality-listing.jpg";

const listingImageMap: Record<string, string> = {
  "claude-code-quality-hero.jpg": claudeCodeListing,
  "vendorland-recognition.png": vendorlandListing,
  "tgi-llama33-hero.png": tgiLlama33Listing,
  "buy-vs-build-hero.png": buyVsBuildListing,
  "llama-3-2-hero.png": llama32Listing,
  "explore-ai-hero.png": exploreAiListing,
  "swift-hero.png": swiftListing,
  "unsloth-hero.png": unslothListing,
  "lora-hero.png": loraListing,
  "gemma-multilingual-hero.png": gemmaListing,
  "multilingual-llms-hero.png": multilingualLlmsListing,
  "llama2-multilingual.png": llama2MultilingualListing,
  "clutch-awards-2023.png": clutchListing,
  "tango-takes-two-hero.png": tangoListing,
  "competent-management-hero.png": competentMgmtListing,
  "chatgpt-competitive-advantage-hero.png": chatgptListing,
  "network-effect-hero.png": networkEffectListing,
  "ai-development-culture.jpg": aiDevCulture,
  "ai-virtuous-cycle.png": aiVirtuousCycle,
  "blue-river-technology.png": blueRiverTech,
  "ai-business-use-case.jpg": aiBusinessListing,
  "ai-designs-dont-work.png": aiDesignsListing,
  "ai-strategies-hero.png": aiStrategiesListing,
};

const CATEGORIES = ["All", "Technology", "Business", "Technology Management"] as const;
const CATEGORIES_DE: Record<string, string> = {
  All: "Alle",
  Technology: "Technologie",
  Business: "Wirtschaft",
  "Technology Management": "Technologiemanagement",
};
const CATEGORIES_FR: Record<string, string> = {
  All: "Tous",
  Technology: "Technologie",
  Business: "Business",
  "Technology Management": "Management technologique",
};
const CATEGORIES_IT: Record<string, string> = {
  All: "Tutti",
  Technology: "Tecnologia",
  Business: "Business",
  "Technology Management": "Management tecnologico",
};
const CATEGORIES_ES: Record<string, string> = {
  All: "Todos",
  Technology: "Tecnología",
  Business: "Negocio",
  "Technology Management": "Gestión tecnológica",
};

export default function Blog() {
  const { t, i18n } = useTranslation();
  const { getLocalizedPath } = useLanguageNavigation();
  const lang = i18n.language?.split("-")[0] || "en";
  const isDe = lang === "de";
  const isFr = lang === "fr";
  const isIt = lang === "it";
  const isEs = lang === "es";
  const dateLocale = isDe ? "de-DE" : isFr ? "fr-FR" : isIt ? "it-IT" : isEs ? "es-ES" : "en-US";

  const [activeCategory, setActiveCategory] = useState<string>("All");

  const filteredPosts = useMemo(() => {
    const sorted = [...blogPosts].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
    if (activeCategory === "All") return sorted;
    return sorted.filter((p) => p.category === activeCategory);
  }, [activeCategory]);

  return (
    <>
      <BlogListJsonLd />
        <section className="py-12 md:py-20">
          <div className="container">
            {/* Page heading — the hub page's only h1; post titles below are h2 */}
            <h1 className="text-3xl font-bold tracking-tight text-foreground sm:text-4xl mb-8">
              {t("blog.title")}
            </h1>

            {/* Category tabs */}
            <div className="flex flex-wrap gap-2 mb-10">
              {CATEGORIES.map((cat) => (
                <button
                  key={cat}
                  onClick={() => setActiveCategory(cat)}
                  className={`px-4 py-2 text-sm font-medium rounded-full border transition-colors ${
                    activeCategory === cat
                      ? "bg-foreground text-background border-foreground"
                      : "bg-transparent text-muted-foreground border-border hover:border-foreground hover:text-foreground"
                  }`}
                >
                  {isDe ? CATEGORIES_DE[cat] : isFr ? CATEGORIES_FR[cat] : isIt ? CATEGORIES_IT[cat] : isEs ? CATEGORIES_ES[cat] : cat}
                </button>
              ))}
            </div>

            {/* Post grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
              {filteredPosts.map((post) => {
                const imgSrc = listingImageMap[post.heroImage];
                return (
                  <a
                    key={post.slug}
                    href={getLocalizedPath(`/blog/${post.slug}`)}
                    className="group block"
                  >
                    {/* Image */}
                    <div className="aspect-[4/3] overflow-hidden rounded-lg mb-4">
                      {imgSrc && (
                        <img
                          src={imgSrc}
                          alt={getLocalizedField(post, "title", lang)}
                          className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                          loading="lazy"
                          decoding="async"
                        />
                      )}
                    </div>

                    {/* Meta */}
                    <div className="flex items-center gap-3 text-xs text-muted-foreground mb-2">
                      <span className="flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        {new Date(post.date).toLocaleDateString(dateLocale, {
                          year: "numeric",
                          month: "short",
                          day: "numeric",
                        })}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {getLocalizedField(post, "readTime", lang)}
                      </span>
                    </div>

                    {/* Category */}
                    <span className="text-xs font-medium text-primary mb-1.5 block">
                      {getLocalizedField(post, "category", lang)}
                    </span>

                    {/* Title */}
                    <h2 className="text-base font-semibold text-foreground leading-snug group-hover:text-primary transition-colors mb-2">
                      {getLocalizedField(post, "title", lang)}
                    </h2>

                    {/* Summary */}
                    <p className="text-sm text-muted-foreground line-clamp-3">
                      {getLocalizedField(post, "summary", lang)}
                    </p>
                  </a>
                );
              })}
            </div>
          </div>
        </section>
    </>
  );
}
